// shared-lib/sink-types.js
// Canonical sink-type contract. Pure functions, no SDK deps.
//
// Two top-level types:
//   - `sink` — bathroom sink. Shape: { type: 'sink', sinkType: 'undermount' | 'vessel' | 'dropin' | 'pedestal' | 'wall-mount' }
//   - `kitchen-sink` — kitchen sink. Shape: { type: 'kitchen-sink', subtype: 'undermount' | 'drop-in' | 'farmhouse' }
//
// `isSink()` covers both, since most generic sink-handling logic (parents,
// countertop mounting, etc.) applies uniformly. Callers that need to
// distinguish should branch on `obj.type` directly.
//
// Legacy `<mount>-sink` type strings are NOT recognized here. The two boundary
// callers that still need legacy↔canonical translation own their own copies of
// the mapping table:
//   - scripts/migrate-sink-types.js (one-shot data migration)
//   - import-assets/uploaders/kohler-firebase-uploader.mjs (third-party data ingest)

const SINK_TYPE = 'sink';
const KITCHEN_SINK_TYPE = 'kitchen-sink';

const SINK_MOUNT_TYPES = [
  'undermount',
  'dropin',
  'vessel',
  'pedestal',
  'wall-mount',
];

// Sink mount types that sit on a countertop surface. The remaining mount types
// (pedestal, wall-mount) are standalone and have no parent object.
//
// 'kitchen' covers legacy kitchen-context sinks (type='sink', sinkType='kitchen'
// — superseded by top-level type='kitchen-sink' but old saved data still uses
// it). Like other countertop-mounted sinks, the parent lower-cabinet is the
// one that participates in collision detection.
//
// KITCHEN SPELLINGS ARE LOAD-BEARING HERE. A kitchen sink stores its mount in
// `subtype` with its own vocabulary — 'drop-in' (hyphenated) and 'farmhouse' —
// and `getSinkType` returns `sinkType || subtype`, so those strings reach this
// list. Both DO sit on a countertop: a farmhouse apron stands proud of the
// slab and opens the cabinet front, which is still a slab. While they were
// missing, `swapSinkModel` read them as standalone and tore the cabinet and
// countertop down on swap — measured live on 3 placed farmhouse sinks, all
// three with a fixtureGroupId and an anchor cabinet. This list answers "does
// this sink sit on a countertop", which is a question about the fixture, not
// about which room it is in.
const COUNTERTOP_MOUNTED_SINK_TYPES = [
  'undermount',
  'dropin',
  'drop-in',
  'farmhouse',
  'vessel',
  'kitchen',
];

function isSink(obj) {
  if (!obj || typeof obj !== 'object') return false;
  if (obj.type === SINK_TYPE || obj.type === KITCHEN_SINK_TYPE) return true;
  if (obj.category && typeof obj.category === 'object') {
    if (obj.category.sink || obj.category['kitchen-sink']) return true;
  }
  return false;
}

function isKitchenSink(obj) {
  if (!obj || typeof obj !== 'object') return false;
  if (obj.type === KITCHEN_SINK_TYPE) return true;
  if (obj.category?.['kitchen-sink']) return true;
  // Legacy shape: type='sink' + sinkType='kitchen'.
  if (obj.type === SINK_TYPE && obj.sinkType === 'kitchen') return true;
  return false;
}

function getSinkType(obj) {
  if (!obj || typeof obj !== 'object') return null;
  // Kitchen sinks store their mount style under `subtype` (GLB-aligned
  // taxonomy). Bathroom sinks use `sinkType`. Either way, prefer the
  // explicit field; fall back to the other for transitional data.
  return obj.sinkType || obj.subtype || null;
}

/**
 * Build a sink's `dimensions` block.
 *
 * Sinks are the only object type in the corpus with no settled shape for this
 * field. Across dev's ~2,400 rooms they appear in SIX different key sets —
 * `[x,y,z]`, `[cutoutDepth,depth,height,width]`, `[cutoutDepth,depth,width]`,
 * `[cutoutDepth,depth,width,x,y,z]`, `[depth,width,x,y,z]`, and the complete
 * one — because each consumer layer grew against whichever shape it met first.
 * One builder is the fix: it makes the halves physically unable to disagree.
 *
 * The seven keys are three groups, not seven independent facts:
 *
 *   x, y, z      The universal object schema. Over 97% of the ~21,000 objects
 *                in dev that carry `dimensions` use exactly these three and
 *                nothing else — lights, tables, cabinets, countertops, chairs,
 *                beds, toilets, faucets. Sinks were the anomaly for LACKING
 *                them, and every generic consumer (`getObjectCorners` /
 *                `getObjectClearanceCorners` in shared-lib/collision, the 3D
 *                resize handles, the properties readout) is right to expect
 *                them.
 *
 *   cutoutDepth  Not a duplicate. How deep the hole through the countertop
 *                slab is cut, which for a model-driven sink is genuinely a
 *                different number from the fixture's overall height (a rim or
 *                tailpiece is part of `height` but not part of the cut).
 *                Defaults to `height` when the caller has only one number;
 *                pass `null` for a sink with no countertop to cut (see below).
 *
 *   width,       THE actual redundancy — exact aliases of x, z, y. Three keys
 *   depth,       too many, and the direction of travel is to drop them. They
 *   height       stay for now only because four files still read them with no
 *                x/y/z fallback: `sinkHelpers.js`, `elementDrawingUtils.js`
 *                (the 2D canvas sink pass), `Countertop.jsx` (the CSG
 *                fallback), and `SceneObject.jsx`. (`sinkPlacementUtils.js`
 *                was a fifth until its dead placement half was deleted; only
 *                `SINK_DEFAULTS` survives there.)
 *
 * So this builder is a WAYPOINT, not doctrine. The destination is
 * `{x, y, z, cutoutDepth}` once those four readers are migrated. Do not add an
 * eighth key here; if you are tempted to, the answer is probably to remove the
 * third instead.
 *
 * Why the x/y/z half is not optional: omitting it does NOT fail in the
 * harmless direction. `getObjectCorners` reads `dimensions.x` / `.z` with no
 * fallback, so the corners come out NaN; every `NaN < NaN` in the SAT is false,
 * no separating axis is ever found, and `doPolygonsOverlap` returns TRUE. A
 * sink with no x/z reports colliding with EVERYTHING — verified directly: such
 * a sink "overlaps" a toilet two metres away. Roughly 290 sinks in dev were in
 * that state when this was written (2026-08-27); the exact count drifts daily
 * as scans land, so treat it as an order of magnitude, not a fact to assert
 * against. Any placement or relayout that collision-checks such a sink gets a
 * spurious failure.
 *
 * The risk of filling these in runs in that same direction: placements that
 * currently FAIL will start passing, and the vertical band in
 * `fixtureCollision.js` goes from a zero half-extent (`dimensions.y || 0`) to a
 * real one, pulling pairs into the XZ test that previously short-circuited.
 * That is the intended correction, not a regression, but it is the thing to
 * watch when this lands.
 *
 * Note what this does NOT fix: 3D rendering. A sink's GLB is scaled by the
 * catalog's `scale` / `scaleFactor`, never by `dimensions`, and positioned by
 * dropping its bounding-box centre on `center`. An earlier version of this
 * comment claimed otherwise.
 *
 * @param {{width:number, depth:number, height:number, cutoutDepth?:?number}} basin
 *   Extents in METERS. `cutoutDepth` omitted → defaults to `height`;
 *   `cutoutDepth: null` → no countertop, no cut.
 * @returns {Object} the complete shape. Garbage in, garbage out: validate
 *   untrusted input (catalog records) before calling.
 */
function buildSinkDimensions({ width, depth, height, cutoutDepth } = {}) {
  // Total by design: it always returns an object. An earlier version returned
  // `null` on a non-numeric basin, which was worse than the bug it guarded —
  // `getObjectCorners` dereferences `dimensions.x` with no null check, so a
  // stored `null` turns a sink that merely over-collides into an uncaught
  // TypeError, and four of the seven call sites assign the result straight
  // into a spread. Callers that read untrusted input (a catalog record)
  // validate before calling; callers building from constants cannot fail.
  //
  // `=== undefined`, not `== null`: a standalone sink (pedestal, wall-mount)
  // has no countertop, so there is no hole and `cutoutDepth` must stay null
  // rather than be back-filled with the fixture's full floor-to-rim height.
  // Those writers pass `null` deliberately; `== null` would have silently
  // overwritten it.
  const depthOfCut = cutoutDepth === undefined ? height : cutoutDepth;
  return {
    width,
    depth,
    height,
    cutoutDepth: depthOfCut,
    x: width,
    y: height,
    z: depth,
  };
}

module.exports = {
  SINK_TYPE,
  KITCHEN_SINK_TYPE,
  SINK_MOUNT_TYPES,
  COUNTERTOP_MOUNTED_SINK_TYPES,
  isSink,
  isKitchenSink,
  getSinkType,
  buildSinkDimensions,
};
